import { create } from 'zustand';
import { STORAGE_KEYS, DEFAULT_SIDEBAR_WIDTH, DEFAULT_FLEET_PANEL_HEIGHT } from '@/utils/constants';

export type Theme = 'dark' | 'light' | 'aurora';

interface UIState {
  theme: Theme;
  sidebarWidth: number;
  routePanelOpen: boolean;
  chartModalOpen: boolean;
  settingsOpen: boolean;
  isMobile: boolean;
  sidebarOpen: boolean;
  isInitialLoading: boolean;
  fleetPanelOpen: boolean;
  fleetData: any[];
  currentExportId: string | null;
  fleetPanelHeight: number;

  setTheme: (theme: Theme) => void;
  setSidebarWidth: (width: number) => void;
  setRoutePanelOpen: (open: boolean) => void;
  setChartModalOpen: (open: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  setIsMobile: (mobile: boolean) => void;
  toggleSidebar: () => void;
  setInitialLoading: (loading: boolean) => void;
  setFleetPanelOpen: (open: boolean, data?: any[]) => void;
  syncFleetTrip: (alias: string, patch: Record<string, unknown>) => void;
  setCurrentExportId: (id: string | null) => void;
  setFleetPanelHeight: (height: number) => void;
}

export const useUIStore = create<UIState>((set) => ({
  theme: (localStorage.getItem(STORAGE_KEYS.THEME) as Theme) || 'dark',
  sidebarWidth: parseInt(localStorage.getItem(STORAGE_KEYS.SIDEBAR_WIDTH) || '', 10) || DEFAULT_SIDEBAR_WIDTH,
  routePanelOpen: false,
  chartModalOpen: false,
  settingsOpen: false,
  isMobile: typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth > 768 : true,
  isInitialLoading: true,
  fleetPanelOpen: false,
  fleetData: [],
  currentExportId: null,
  fleetPanelHeight: DEFAULT_FLEET_PANEL_HEIGHT,

  setTheme: (theme) => {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
    document.documentElement.setAttribute('data-theme', theme === 'dark' ? '' : theme);
    set({ theme });
  },

  setSidebarWidth: (width) => {
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_WIDTH, String(width));
    set({ sidebarWidth: width });
  },

  setRoutePanelOpen: (open) => set({ routePanelOpen: open }),
  setChartModalOpen: (open) => set({ chartModalOpen: open }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),

  setIsMobile: (mobile) =>
    set({ isMobile: mobile, sidebarOpen: !mobile }),

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setInitialLoading: (loading) => set({ isInitialLoading: loading }),
  setFleetPanelOpen: (open, data) => set((state) => ({ 
    fleetPanelOpen: open, 
    fleetData: data || state.fleetData 
  })),
  syncFleetTrip: (alias, patch) => set((state) => {
    const normalizedAlias = alias.trim();
    if (!normalizedAlias || state.fleetData.length === 0) return state;

    let didUpdate = false;
    const fleetData = state.fleetData.map((trip) => {
      if (String(trip?.alias ?? '').trim() !== normalizedAlias) return trip;
      didUpdate = true;
      return { ...trip, ...patch };
    });

    return didUpdate ? { fleetData } : state;
  }),
  setCurrentExportId: (id) => set({ currentExportId: id }),
  setFleetPanelHeight: (height) => set({ fleetPanelHeight: height }),
}));
